import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail, Phone, Check } from "lucide-react";
import { GENERAL_PHONE, trackPhoneClick } from "@/config/contact";

const bullets = [
  "In 20 minuten een helder beeld van het platform",
  "Toegespitst op jouw school en leerjaren",
  "Vrijblijvend, geen verplichtingen",
];

const CtaSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-border bg-card shadow-large">
            <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
              {/* left: copy on brand gradient */}
              <div className="relative p-10 lg:p-14 bg-gradient-to-br from-primary via-primary to-[hsl(199,100%,38%)] overflow-hidden">
                <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-primary-foreground/10 blur-3xl" />
                <div
                  className="absolute inset-0 opacity-[0.18]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, hsl(var(--primary-foreground)/0.25) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary-foreground)/0.25) 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                    maskImage:
                      "radial-gradient(ellipse 80% 70% at 20% 10%, black 20%, transparent 100%)",
                    WebkitMaskImage:
                      "radial-gradient(ellipse 80% 70% at 20% 10%, black 20%, transparent 100%)",
                  }}
                />
                <div className="relative">
                  <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground">
                    Kennismaken
                  </span>
                  <h2 className="mt-6 text-3xl sm:text-4xl lg:text-[2.9rem] font-extrabold text-primary-foreground leading-[1.08] [text-wrap:balance]">
                    Ontdek wat studie connect voor jullie LOB-programma kan betekenen.
                  </h2>
                  <p className="mt-5 max-w-lg text-primary-foreground/85 text-lg leading-relaxed">
                    In een kennismaking laten we het platform zien en kijken we samen
                    hoe het aansluit op jullie leerlijn.
                  </p>

                  <ul className="mt-8 space-y-3">
                    {bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-primary-foreground/90 text-sm font-medium"
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-foreground/20">
                          <Check className="h-3 w-3" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-extrabold gap-2 text-base px-7 py-6 rounded-2xl shadow-large hover:-translate-y-0.5 transition-all"
                      onClick={() => navigate("/demo")}
                    >
                      Plan een kennismaking
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-primary-foreground/35 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 font-bold text-base px-7 py-6 rounded-2xl"
                      onClick={() => navigate("/contact")}
                    >
                      Stel een vraag
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/prijzen")}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/85 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60 rounded"
                  >
                    Bekijk de tarieven <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* right: contact panel */}
              <div className="relative flex flex-col justify-center gap-6 p-10 lg:p-12 bg-card">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                    Je vaste aanspreekpunt
                  </p>
                  <div className="mt-5 flex items-center gap-4">
                    <img
                      src="/wesley-van-komen.png"
                      alt="Wesley van Komen, Strategy &amp; Sales bij studie connect"
                      loading="lazy"
                      className="h-16 w-16 shrink-0 rounded-2xl object-cover"
                    />
                    <div className="min-w-0">
                      <p className="text-lg font-extrabold text-secondary truncate">
                        Wesley van Komen
                      </p>
                      <p className="text-xs font-bold uppercase tracking-wide text-primary">
                        Strategy &amp; Sales
                      </p>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
                    Liever direct sparren over LOB op jouw school? Stuur Wesley
                    een mail of bel ons rechtstreeks.
                  </p>
                </div>

                <div className="space-y-3">
                  <a
                    href="mailto:wvankomen@studie-connect.nl"
                    className="flex items-center gap-3 rounded-2xl border border-border px-4 py-3.5 text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors break-all"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-primary" />
                    wvankomen@studie-connect.nl
                  </a>
                  <a
                    href={GENERAL_PHONE.href}
                    aria-label={GENERAL_PHONE.ariaLabel}
                    onClick={() => trackPhoneClick("homepage_contact_cta")}
                    className="flex items-center gap-3 rounded-2xl border border-border px-4 py-3.5 text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors"
                  >
                    <Phone className="h-4 w-4 shrink-0 text-primary" />
                    {GENERAL_PHONE.display}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
